import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDevices, selectDevices, selectDevicesLoading, selectDevicesError } from '../../redux/slices/devices';
import { Device } from '../../types';
import { Card, Spinner, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaEdit, FaCircle } from 'react-icons/fa';
import { ThunkDispatch, Action } from '@reduxjs/toolkit';
import { RootState } from '../../redux/rootReducer';

const DeviceList: React.FC = () => {
  const dispatch = useDispatch<ThunkDispatch<RootState, void, Action>>();
  const devices = useSelector(selectDevices);
  const loading = useSelector(selectDevicesLoading);
  const error = useSelector(selectDevicesError);

  useEffect(() => {
    dispatch(fetchDevices());
  }, [dispatch]);

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading devices...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error loading devices: {error}
      </Alert>
    );
  }

  if (!devices || devices.length === 0) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <h4 className="text-muted mb-3">No Devices Found</h4>
          <p className="text-muted mb-4">
            Get started by connecting your first device to your organization.
          </p>
          <Link to="/devices/connect" className="btn btn-primary">
            Connect New Device
          </Link>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="row g-4">
      {devices.map((device: Device) => (
        <div key={device.id} className="col-md-6 col-lg-4">
          <Card className="h-100">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <h5 className="card-title mb-1">{device.name || 'Unnamed Device'}</h5>
                  <p className="text-muted mb-0 small">{device.serialNumber}</p>
                </div>
                <Link to={`/devices/edit/${device.id}`} className="btn btn-sm btn-outline-secondary">
                  <FaEdit />
                </Link>
              </div>
              
              <div className="d-flex align-items-center mb-2">
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: device.colorCode || '#6c757d',
                  borderRadius: '50%',
                  marginRight: '8px'
                }} />
                <span className="text-muted">
                  {device.inClinicLocation || 'No Location'}
                  {device.inClinicLocationNumber && ` #${device.inClinicLocationNumber}`}
                </span>
              </div>

              <div className="d-flex align-items-center">
                <FaCircle 
                  className={device.active ? 'text-success' : 'text-danger'} 
                  style={{ fontSize: '0.8rem', marginRight: '6px' }} 
                />
                <small className={device.active ? 'text-success' : 'text-danger'}>
                  {device.active ? 'Active' : 'Inactive'}
                </small>
              </div>
            </Card.Body>
          </Card>
        </div>
      ))}
    </div>
  );
};

export default DeviceList;
